import {
  GPS_ACCURACY_HIGH_MAX_ALLOWED_METERS,
  GPS_ACCURACY_MEDIUM_MAX_ALLOWED_METERS,
  type GpsAccuracyHighMaxMeters,
  type GpsAccuracyMediumMaxMeters,
} from "../data/gps-accuracy-bands";

/**
 * Platform cockpit chips for the account GPS accuracy band ceilings (2026-08-28 lock).
 *
 * Two rows, one rule: a Medium chip that does not sit strictly above the chosen High chip is
 * disabled rather than hidden, so the operator can see why High 50 leaves only 75 / 100 / 150.
 * Picking a High chip that overtakes the current Medium keeps Medium as drafted; the editor's
 * save gate then refuses the pair with the same words the host would use.
 */
export function GpsAccuracyBandPicker({
  gpsAccuracyHighMaxMeters: high,
  gpsAccuracyMediumMaxMeters: medium,
  onChange,
}: {
  gpsAccuracyHighMaxMeters: GpsAccuracyHighMaxMeters;
  gpsAccuracyMediumMaxMeters: GpsAccuracyMediumMaxMeters;
  onChange: (next: {
    gpsAccuracyHighMaxMeters: GpsAccuracyHighMaxMeters;
    gpsAccuracyMediumMaxMeters: GpsAccuracyMediumMaxMeters;
  }) => void;
}) {
  return (
    <fieldset className="tpl__fieldset ping-interval">
      <legend className="tpl__legend">GPS accuracy bands</legend>
      <p className="setup__field-hint">
        Labels a reported ±metres as High, Medium, or Low on the Timeclock report and Command
        Live identity cards. Changes the words, not the phones: capture accuracy is unchanged.
      </p>
      <p className="setup__field-hint">High: up to</p>
      <div className="shift-duration__presets" role="group" aria-label="GPS accuracy High ceiling options">
        {GPS_ACCURACY_HIGH_MAX_ALLOWED_METERS.map((option) => (
          <button
            key={`high-${option}`}
            type="button"
            className={`toggle-chip${high === option ? " toggle-chip--on" : ""}`}
            aria-pressed={high === option}
            onClick={() => onChange({ gpsAccuracyHighMaxMeters: option, gpsAccuracyMediumMaxMeters: medium })}
          >
            {option} m
          </button>
        ))}
      </div>
      <p className="setup__field-hint">Medium: up to (must be above High; beyond it reads Low)</p>
      <div className="shift-duration__presets" role="group" aria-label="GPS accuracy Medium ceiling options">
        {GPS_ACCURACY_MEDIUM_MAX_ALLOWED_METERS.map((option) => {
          const legal = option > high;
          return (
            <button
              key={`medium-${option}`}
              type="button"
              className={`toggle-chip${medium === option ? " toggle-chip--on" : ""}`}
              aria-pressed={medium === option}
              disabled={!legal}
              title={legal ? undefined : `Medium must be above the ${high} m High ceiling`}
              onClick={() => onChange({ gpsAccuracyHighMaxMeters: high, gpsAccuracyMediumMaxMeters: option })}
            >
              {option} m
            </button>
          );
        })}
      </div>
    </fieldset>
  );
}
